import React from 'react';
import Icon from '../../../components/AppIcon';

const WorkStyleSection = ({ profile }) => {
  const formatWorkStyle = (style) => {
    if (!style) return '';
    return style.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  
  // Helper function to add descriptive labels based on style values
  const getStyleDescription = (type, value) => {
    const descriptions = {
      work_pace: {
        fast_paced: 'Quick turnaround, high efficiency',
        balanced: 'Steady progress with balanced approach',
        methodical: 'Thorough and detail-oriented process'
      },
      feedback_preference: {
        detailed: 'Appreciates comprehensive feedback',
        regular: 'Prefers consistent, ongoing feedback',
        minimal: 'Works independently with limited guidance'
      },
      collaboration_style: {
        independent: 'Self-directed with minimal supervision',
        collaborative: 'Team-oriented, enjoys shared projects',
        adaptable: 'Flexible to work in any environment'
      },
      problem_solving_approach: {
        analytical: 'Logical, data-driven decisions',
        creative: 'Innovative, outside-the-box thinking',
        practical: 'Efficient, realistic solutions',
        innovative: 'Forward-thinking, novel approaches'
      }
    };
    
    return descriptions[type]?.[value] || '';
  };

  // If there are no work style fields filled out, don't display this section
  if (!profile?.work_pace && !profile?.feedback_preference && 
      !profile?.collaboration_style && !profile?.problem_solving_approach && 
      !profile?.work_style_description) {
    return null;
  }

  return (
    <section className="content-container ">
      <div className="mx-auto py-12">
        <div className="flex items-center mb-8">
          <svg className="w-6 h-6 text-primary mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h2 className="text-2xl font-semibold text-gray-800">My Work Style</h2>
        </div>
        <p className="text-text-secondary mb-12">Understanding how I work helps us collaborate more effectively. Here's my preferred approach:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 pt-8">
          {profile?.work_pace && (
            <div className="p-5 bg-surface rounded-lg shadow-sm border border-border flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 -mt-12">
                <Icon name="Zap" size={24} className="text-primary" />
              </div>
              <h3 className="font-medium text-text-primary mb-2">Work Pace</h3>
              <p className="text-text-secondary font-medium text-center">{formatWorkStyle(profile.work_pace)}</p>
              <p className="text-text-secondary text-sm mt-1 text-center">{getStyleDescription('work_pace', profile.work_pace)}</p>
            </div>
          )}          
          {profile?.feedback_preference && (
            <div className="p-5 bg-surface rounded-lg shadow-sm border border-border flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 -mt-12">
                <Icon name="MessageCircle" size={24} className="text-primary" />
              </div>
              <h3 className="font-medium text-text-primary mb-2">Feedback Style</h3>
              <p className="text-text-secondary font-medium text-center">{formatWorkStyle(profile.feedback_preference)}</p>
              <p className="text-text-secondary text-sm mt-1 text-center">{getStyleDescription('feedback_preference', profile.feedback_preference)}</p>
            </div>
          )}
          
          {profile?.collaboration_style && (
            <div className="p-5 bg-surface rounded-lg shadow-sm border border-border flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 -mt-12">
                <Icon name="Users" size={24} className="text-primary" />
              </div>
              <h3 className="font-medium text-text-primary mb-2">Collaboration</h3>
              <p className="text-text-secondary font-medium text-center">{formatWorkStyle(profile.collaboration_style)}</p>
              <p className="text-text-secondary text-sm mt-1 text-center">{getStyleDescription('collaboration_style', profile.collaboration_style)}</p>
            </div>
          )}
          
          {profile?.problem_solving_approach && (
            <div className="p-5 bg-surface rounded-lg shadow-sm border border-border flex flex-col items-center">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-4 -mt-12">
                <Icon name="Lightbulb" size={24} className="text-primary" />
              </div>
              <h3 className="font-medium text-text-primary mb-2">Problem Solving</h3>
              <p className="text-text-secondary font-medium text-center">{formatWorkStyle(profile.problem_solving_approach)}</p>
              <p className="text-text-secondary text-sm mt-1 text-center">{getStyleDescription('problem_solving_approach', profile.problem_solving_approach)}</p>
            </div>
          )}
        </div>
        
        {profile?.work_style_description && (
          <div className="bg-surface p-6 rounded-lg shadow-sm border border-border">
            <h3 className="font-medium text-text-primary mb-3">How I Work</h3>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-primary flex-shrink-0 mr-3 mt-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <p className="text-text-secondary italic">{profile.work_style_description}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WorkStyleSection;

